/* =========================================
   Custom Dynamic Header
   Normal at top, fixed only after scroll-away.
   Mirrored from the known-good Aurora build 155533803685.
========================================= */

document.addEventListener('DOMContentLoaded', () => {
  const header = document.querySelector('.shopify-section-header');
  if (!header) return;

  header.classList.add('fc-header-entry-prep');

  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      header.classList.add('fc-header-entry-ready');

      window.setTimeout(() => {
        header.classList.remove('fc-header-entry-prep', 'fc-header-entry-ready');
      }, 1100);
    });
  });

  /*
    Fully disable Aurora's native dynamic sticky state.
    Our custom header logic is the only source of truth.
  */
  header.removeAttribute('dynamic-sticky');
  header.removeAttribute('aria-hidden');
  header.classList.remove('scrolled');

  const lockAuroraHeaderBehavior = () => {
    const auroraHeader = document.querySelector('header-component');
    if (!auroraHeader) return;

    auroraHeader.isDynamicSticky = false;
    auroraHeader.isHidden = false;
    auroraHeader.isDynamicShowLocked = true;

    if (typeof auroraHeader.toggleLockDynamicShow === 'function') {
      auroraHeader.toggleLockDynamicShow(true);
    }

    if (typeof auroraHeader.setHidden === 'function') {
      auroraHeader.setHidden = () => {};
    }
  };

  lockAuroraHeaderBehavior();

  if (window.customElements && customElements.whenDefined) {
    customElements.whenDefined('header-component').then(lockAuroraHeaderBehavior);
  }

  const auroraStickyObserver = new MutationObserver(() => {
    if (header.hasAttribute('dynamic-sticky')) {
      header.removeAttribute('dynamic-sticky');
    }

    if (header.hasAttribute('aria-hidden')) {
      header.removeAttribute('aria-hidden');
    }

    if (header.classList.contains('scrolled')) {
      header.classList.remove('scrolled');
    }
  });

  auroraStickyObserver.observe(header, {
    attributes: true,
    attributeFilter: ['dynamic-sticky', 'aria-hidden', 'class'],
  });

  let placeholder = document.querySelector('.fc-dynamic-header-placeholder');

  if (!placeholder) {
    placeholder = document.createElement('div');
    placeholder.className = 'fc-dynamic-header-placeholder';
    header.parentNode.insertBefore(placeholder, header.nextSibling);
  }

  let headerHeight = 0;
  let headerStart = 0;
  let headerEnd = 0;

  let lastScrollY = Math.max(window.scrollY || 0, 0);
  let ticking = false;
  let isFixed = false;
  let isHidden = false;
  let isAnimatingHeader = false;
  let pendingHeaderState = null;
  let accumulated = 0;
  let lastDirection = null;
  let lastStateChangeTime = 0;
  let mobileIntentTimer = null;
  let mobileIntentDirection = null;

  const desktopMinimumScrollDelta = 1;
  const mobileMinimumScrollDelta = 2;
  const topResetPoint = 2;
  const activationBuffer = 18;
  const desktopShowDistance = 1;
  const desktopHideDistance = 1;
  const mobileIntentDelay = 90;
  const mobileStateCooldown = 760;

  function isMobileHeaderMode() {
    return window.matchMedia('(max-width: 749px)').matches;
  }

  function getMinimumScrollDelta() {
    return isMobileHeaderMode() ? mobileMinimumScrollDelta : desktopMinimumScrollDelta;
  }

  function afterPaint(callback) {
    requestAnimationFrame(() => {
      requestAnimationFrame(callback);
    });
  }

  function measureHeader() {
    const wasFixed = isFixed;
    const wasHidden = isHidden;

    if (wasFixed) {
      header.classList.add('fc-dynamic-header-no-transition');
      header.classList.remove('fc-dynamic-header-fixed', 'fc-dynamic-header-hidden');
      placeholder.classList.remove('is-active');
    }

    headerHeight = Math.ceil(header.offsetHeight || 0);
    headerStart = header.offsetTop || 0;
    headerEnd = headerStart + headerHeight;

    document.documentElement.style.setProperty('--fc-dynamic-header-height', `${headerHeight}px`);

    if (wasFixed) {
      placeholder.classList.add('is-active');
      header.classList.add('fc-dynamic-header-fixed');

      if (wasHidden) {
        header.classList.add('fc-dynamic-header-hidden');
      }

      afterPaint(() => {
        header.classList.remove('fc-dynamic-header-no-transition');
      });
    }
  }

  function activateFixedHidden() {
    if (isFixed) return;

    isFixed = true;
    isHidden = true;
    isAnimatingHeader = false;
    accumulated = 0;
    lastDirection = null;

    placeholder.classList.add('is-active');

    header.classList.add('fc-dynamic-header-no-transition');
    header.classList.add('fc-dynamic-header-fixed', 'fc-dynamic-header-hidden');

    afterPaint(() => {
      header.classList.remove('fc-dynamic-header-no-transition');
    });
  }

  function finishHeaderAnimation() {
    window.clearTimeout(header._fcAnimationTimer);

    const complete = () => {
      window.clearTimeout(header._fcAnimationTimer);
      header.removeEventListener('transitionend', onEnd);

      isAnimatingHeader = false;
      header.classList.remove('fc-dynamic-header-animating');

      const nextState = pendingHeaderState;
      pendingHeaderState = null;

      if (nextState === 'visible' && isHidden) {
        showHeader();
      }

      if (nextState === 'hidden' && !isHidden) {
        hideHeader();
      }
    };

    const onEnd = event => {
      if (event.target !== header) return;
      if (event.propertyName !== 'transform') return;
      complete();
    };

    header.addEventListener('transitionend', onEnd);
    header._fcAnimationTimer = window.setTimeout(complete, 740);
  }

  function showHeader() {
    if (!isFixed || !isHidden) return;

    if (isAnimatingHeader) {
      pendingHeaderState = 'visible';
      return;
    }

    isHidden = false;
    isAnimatingHeader = true;

    window.clearTimeout(header._fcAnimationTimer);

    header.classList.remove('fc-dynamic-header-no-transition');
    header.classList.add('fc-dynamic-header-animating');

    requestAnimationFrame(() => {
      header.classList.remove('fc-dynamic-header-hidden');
      finishHeaderAnimation();
    });
  }

  function hideHeader() {
    if (!isFixed || isHidden) return;

    if (isAnimatingHeader) {
      pendingHeaderState = 'hidden';
      return;
    }

    isHidden = true;
    isAnimatingHeader = true;

    window.clearTimeout(header._fcAnimationTimer);

    header.classList.remove('fc-dynamic-header-no-transition');
    header.classList.add('fc-dynamic-header-animating');

    requestAnimationFrame(() => {
      header.classList.add('fc-dynamic-header-hidden');
      finishHeaderAnimation();
    });
  }

  function resetToNormal() {
    if (!isFixed) return;

    isFixed = false;
    isHidden = false;
    isAnimatingHeader = false;
    accumulated = 0;
    lastDirection = null;

    header.classList.add('fc-dynamic-header-no-transition');
    header.classList.remove('fc-dynamic-header-fixed', 'fc-dynamic-header-hidden');
    placeholder.classList.remove('is-active');

    afterPaint(() => {
      header.classList.remove('fc-dynamic-header-no-transition');
    });
  }

  function updateHeader() {
    const currentScrollY = Math.max(window.scrollY || 0, 0);
    const diff = currentScrollY - lastScrollY;

    if (Math.abs(diff) < getMinimumScrollDelta()) {
      ticking = false;
      return;
    }

    const direction = diff > 0 ? 'down' : 'up';
    const isMobileMode = isMobileHeaderMode();

    if (currentScrollY <= topResetPoint) {
      window.clearTimeout(mobileIntentTimer);
      mobileIntentTimer = null;
      mobileIntentDirection = null;

      resetToNormal();

      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    if (!isFixed && currentScrollY <= headerEnd + activationBuffer) {
      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    if (!isFixed && currentScrollY > headerEnd + activationBuffer) {
      activateFixedHidden();

      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    if (!isMobileMode) {
      if (isAnimatingHeader) {
        pendingHeaderState = direction === 'up' ? 'visible' : 'hidden';

        lastScrollY = currentScrollY;
        ticking = false;
        return;
      }

      if (direction !== lastDirection) {
        lastDirection = direction;
        accumulated = 0;
      }

      accumulated += Math.abs(diff);

      if (direction === 'up' && isHidden && accumulated >= desktopShowDistance) {
        accumulated = 0;
        showHeader();
      }

      if (direction === 'down' && !isHidden && accumulated >= desktopHideDistance) {
        accumulated = 0;
        hideHeader();
      }

      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    if (isAnimatingHeader) {
      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    const now = Date.now();

    if (now - lastStateChangeTime < mobileStateCooldown) {
      lastScrollY = currentScrollY;
      ticking = false;
      return;
    }

    if (mobileIntentDirection !== direction) {
      window.clearTimeout(mobileIntentTimer);
      mobileIntentTimer = null;
      mobileIntentDirection = direction;
    }

    if (!mobileIntentTimer) {
      mobileIntentTimer = window.setTimeout(() => {
        mobileIntentTimer = null;

        if (!isFixed || isAnimatingHeader) return;

        lastStateChangeTime = Date.now();

        if (mobileIntentDirection === 'up' && isHidden) {
          showHeader();
        }

        if (mobileIntentDirection === 'down' && !isHidden) {
          hideHeader();
        }
      }, mobileIntentDelay);
    }

    lastScrollY = currentScrollY;
    ticking = false;
  }

  function requestUpdate() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(updateHeader);
    }
  }

  measureHeader();

  window.addEventListener('scroll', requestUpdate, { passive: true });

  window.addEventListener('resize', () => {
    measureHeader();
    requestUpdate();
  });
});
